import {
  CLEAR_FIELD_ACTION,
  FOCUS_FIELD_ACTION,
  FRAME_NAMES,
  MAIN_FRAME,
  TOKENIZE_ACTION,
  STYLE_FIELD_ACTION,
  CHECK_CVV_ACTION,
  SET_PLACEHOLDERS_ACTION,
  REVALIDATE_FIELD,
  SET_CARD_SCHEME_ACTION,
} from "./constants";
import { createIframe } from "./utils/tangoUtils";
import * as actions from "./actions";
import { TokenizeAdditionalProps } from "./api/tokenizeTypes";
import {
  RequestToCheckCVVProps,
  RequestToTokenizeProps,
} from "./fields/card/types";
import { CheckCvvRequestPayload } from "./api/checkCvvTypes";

type FrameNames = keyof typeof FRAME_NAMES;

type Actions = typeof actions;

type Listener = {
  [P in keyof Actions]: (
    tangoInstance: TangoPayment,
    payload: ReturnType<Actions[P]>["payload"]
  ) => void;
};

export class TangoPayment {
  private readonly url: string;
  private readonly urlOrigin: string;
  public iframes: Record<
    string,
    Partial<Record<FrameNames, HTMLIFrameElement>>
  > = {};

  private mainFrame: HTMLIFrameElement;
  private listeners: Partial<Listener>;
  private option: string;
  actions = actions;

  iframeListener: (event: MessageEvent) => void;

  constructor(url: string, defaultOption: string) {
    this.url = url;

    const urlInstance = new URL(url);
    this.urlOrigin = urlInstance.origin;
    this.option = defaultOption;
    this.iframes[this.option] = { ...this.iframes[this.option] };
  }

  switchOption(id: string) {
    this.option = id;
    this.iframes[this.option] = { ...this.iframes[this.option] };
  }

  init($el: HTMLElement) {
    this.mainFrame = createIframe(`${this.url}/${MAIN_FRAME}.html`, MAIN_FRAME);
    $el.appendChild(this.mainFrame);

    this.listenMassagesFromIframes = this.listenMassagesFromIframes.bind(this);
    window.addEventListener("message", this.listenMassagesFromIframes);

    return this;
  }

  listenMassagesFromIframes(
    event: MessageEvent<ReturnType<Actions[keyof Actions]>>
  ) {
    const { data, origin } = event;

    if (origin !== this.urlOrigin) {
      return;
    }

    if (data.type in actions) {
      // @ts-ignore
      this.listeners[data.type]?.(this, data.payload);
    }

    if (data.type === actions.setCardSchemeAction.getType()) {
      this.notifyCvvFieldOfScheme(data.payload);
    }
  }

  notifyCvvFieldOfScheme(scheme?: string) {
    this.iframes[this.option].cvv.contentWindow.postMessage(
      {
        type: SET_CARD_SCHEME_ACTION,
        data: scheme,
      },
      this.urlOrigin
    );
  }

  listenTo<A extends Actions[keyof Actions]>(
    action: A,
    callback: (
      tangoInstance: TangoPayment,
      payload: ReturnType<A>["payload"]
    ) => void
  ) {
    this.listeners = { ...this.listeners, [action.getType()]: callback };
  }

  unsubscribe(action: Actions[keyof Actions]) {
    this.listeners = { ...this.listeners, [action.getType()]: undefined };
  }

  initAllFieldFrames() {
    Object.values(FRAME_NAMES).forEach((name: FrameNames) => {
      this.initFieldFrame(name);
    });

    return this;
  }

  initFieldFrame(name: FrameNames) {
    this.iframes[this.option][name] = createIframe(
      `${this.url}/${name}.html`,
      name
    );
    return this;
  }

  setPlaceholders(placeholders: Partial<Record<FrameNames, string>>) {
    Object.entries(placeholders).forEach(
      ([frameName, placeholder]: [Partial<FrameNames>, string]) => {
        this.iframes[this.option][frameName]?.contentWindow?.postMessage(
          {
            type: SET_PLACEHOLDERS_ACTION,
            payload: placeholder,
          },
          this.urlOrigin
        );
      }
    );
  }

  renderFrame($el: HTMLElement, name: FrameNames) {
    const frame = this.iframes[this.option][name];
    if (!frame) {
      throw new Error(
        `iframe [${name}] hasn't initialized yet. Call .initFieldFrame or .initAllFieldFrames before`
      );
    }

    $el.appendChild(frame);
  }

  renderFrames(renderConfig: Partial<Record<FrameNames, HTMLElement>>) {
    Object.entries(renderConfig).forEach(
      ([name, $el]: [FrameNames, HTMLElement]) => {
        this.renderFrame($el, name);
      }
    );
  }

  applyStyle(
    fieldsStyle: Partial<Record<FrameNames, Partial<CSSStyleDeclaration>>>
  ) {
    Object.entries(this.iframes[this.option]).forEach(
      ([frameName, frame]: [Partial<FrameNames>, HTMLIFrameElement]) => {
        const fieldStyle = fieldsStyle[frameName];

        if (!fieldStyle) {
          return;
        }

        frame?.contentWindow?.postMessage(
          { type: STYLE_FIELD_ACTION, data: fieldStyle },
          this.urlOrigin
        );
      }
    );
  }

  tokenize(payload: TokenizeAdditionalProps, hasToMock?: boolean) {
    const messageData: RequestToTokenizeProps = {
      type: TOKENIZE_ACTION,
      payload,
      options: { hasToMock },
    };
    this.mainFrame?.contentWindow?.postMessage(messageData, this.urlOrigin);
  }

  checkCvv(payload: Omit<CheckCvvRequestPayload, "cvv">, hasToMock?: boolean) {
    const messageData: RequestToCheckCVVProps = {
      type: CHECK_CVV_ACTION,
      payload,
      options: { hasToMock },
    };
    this.mainFrame?.contentWindow?.postMessage(messageData, this.urlOrigin);
  }

  clearFields(fields?: FrameNames[]) {
    const fieldNames = fields ?? (Object.keys(FRAME_NAMES) as FrameNames[]);

    fieldNames.forEach((field) => {
      this.iframes[this.option][field]?.contentWindow?.postMessage(
        { type: CLEAR_FIELD_ACTION },
        this.urlOrigin
      );
    });
  }

  focus(fieldName: FrameNames) {
    this.iframes?.[this.option]?.[fieldName]?.contentWindow.postMessage(
      { type: FOCUS_FIELD_ACTION },
      this.urlOrigin
    );
  }

  requestCardValidation() {
    const fieldNames = Object.keys(FRAME_NAMES) as FrameNames[];
    fieldNames.forEach((field) => {
      this.iframes[this.option][field]?.contentWindow?.postMessage(
        { type: REVALIDATE_FIELD },
        this.urlOrigin
      );
    });
  }

  destroy() {
    this.mainFrame?.parentNode?.removeChild(this.mainFrame);
    window.removeEventListener("message", this.listenMassagesFromIframes);

    Object.values(this.iframes[this.option]).forEach((iframe) => {
      iframe.parentNode?.removeChild(iframe);
    });

    Object.values(this.actions).forEach((action) => {
      this.unsubscribe(action);
    });

    if (this.iframeListener) {
      window.removeEventListener("message", this.iframeListener);
    }
  }
}

window.TangoPayment = TangoPayment;
